/**
 * Language Detection Service
 *
 * Smart language detection that combines:
 * 1. Device language (navigator.language)
 * 2. IP-based geolocation
 * 3. User preferences (localStorage, database)
 * 4. Falls back to English
 *
 * Priority Order:
 * 1. URL parameter (?lng=en)
 * 2. localStorage (user's previous choice)
 * 3. Device language (navigator.language)
 * 4. IP-based geolocation → language mapping
 * 5. English fallback
 */

// Mapping of country codes to preferred languages
const COUNTRY_TO_LANGUAGE = {
  // English-speaking countries
  'US': 'en', 'GB': 'en', 'AU': 'en', 'CA': 'en', 'NZ': 'en', 'IE': 'en', 'ZA': 'en',

  // European languages
  'IT': 'it', 'ES': 'es', 'DE': 'de', 'AT': 'de', 'CH': 'de', // Swiss German
  'FR': 'fr', 'BE': 'fr', 'GR': 'el', 'PT': 'pt', 'BR': 'pt',
  'RU': 'ru', 'UA': 'ru',

  // Asian languages
  'CN': 'zh', 'TW': 'zh', 'HK': 'zh', 'JP': 'ja', 'IR': 'fa',

  // Middle Eastern
  'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'IL': 'ar', 'JO': 'ar',
};

// Browser language codes to i18n language codes
const BROWSER_LANG_TO_I18N = {
  'en': 'en', 'en-US': 'en', 'en-GB': 'en',
  'de': 'de', 'de-DE': 'de', 'de-AT': 'de', 'de-CH': 'de',
  'it': 'it', 'it-IT': 'it',
  'es': 'es', 'es-ES': 'es', 'es-MX': 'es',
  'fr': 'fr', 'fr-FR': 'fr', 'fr-CA': 'fr',
  'pt': 'pt', 'pt-BR': 'pt', 'pt-PT': 'pt',
  'el': 'el', 'el-GR': 'el',
  'zh': 'zh', 'zh-CN': 'zh', 'zh-TW': 'zh',
  'ja': 'ja', 'ja-JP': 'ja',
  'ru': 'ru', 'ru-RU': 'ru',
  'fa': 'fa', 'fa-IR': 'fa',
  'ar': 'ar', 'ar-SA': 'ar', 'ar-EG': 'ar',
};

const SUPPORTED_LANGUAGES = ['en', 'it', 'es', 'de', 'fr', 'el', 'pt', 'zh', 'ja', 'ru', 'fa', 'ar'];

/**
 * Get language from browser's navigator.language
 * @returns {string} Language code (e.g., 'en', 'de', 'it')
 */
export const getBrowserLanguage = () => {
  const navigatorLang = navigator.language || navigator.userLanguage;
  const mappedLang = BROWSER_LANG_TO_I18N[navigatorLang];

  if (mappedLang && SUPPORTED_LANGUAGES.includes(mappedLang)) {
    return mappedLang;
  }

  // Try just the language part (e.g., 'en' from 'en-US')
  const langPart = navigatorLang.split('-')[0].toLowerCase();
  if (BROWSER_LANG_TO_I18N[langPart] && SUPPORTED_LANGUAGES.includes(BROWSER_LANG_TO_I18N[langPart])) {
    return BROWSER_LANG_TO_I18N[langPart];
  }

  return null;
};

/**
 * Get user's language from localStorage
 * @returns {string|null} Language code or null
 */
export const getStoredLanguage = () => {
  const stored = localStorage.getItem('scrolled_preferred_language');
  return stored && SUPPORTED_LANGUAGES.includes(stored) ? stored : null;
};

/**
 * Fetch country from IP geolocation
 * Uses multiple free geolocation services as fallback
 * @returns {Promise<string>} Country code (e.g., 'DE', 'US')
 */
export const getCountryFromIP = async () => {
  try {
    // Try ip-api.com (most reliable free service)
    try {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.country_code) {
          console.log(`[Language Detection] IP Geolocation detected country: ${data.country_code}`);
          return data.country_code.toUpperCase();
        }
      }
    } catch (err1) {
      console.log('[Language Detection] ip-api.co failed, trying backup...');

      // Fallback to another service
      const backupResponse = await fetch('https://ipwhois.app/json/');
      if (backupResponse.ok) {
        const data = await backupResponse.json();
        if (data.country_code) {
          console.log(`[Language Detection] Backup geolocation detected country: ${data.country_code}`);
          return data.country_code.toUpperCase();
        }
      }
    }
  } catch (error) {
    console.warn('[Language Detection] IP geolocation failed:', error.message);
  }

  return null;
};

/**
 * Get language from country code
 * @param {string} countryCode - Country code (e.g., 'DE')
 * @returns {string|null} Language code or null
 */
export const getLanguageFromCountry = (countryCode) => {
  if (!countryCode) return null;

  const lang = COUNTRY_TO_LANGUAGE[countryCode.toUpperCase()];

  if (lang && SUPPORTED_LANGUAGES.includes(lang)) {
    return lang;
  }

  return null;
};

/**
 * Smart language detection with English as default
 *
 * Detection order:
 * 1. URL parameter (?lng=en)
 * 2. localStorage (user's previous explicit choice)
 * 3. English fallback (ALWAYS)
 *
 * NOTE: Browser language and IP geolocation are intentionally DISABLED
 * to ensure users always see English unless they explicitly choose otherwise.
 * This prevents automatic German/other language selection from system settings.
 *
 * @returns {Promise<Object>} { language: 'en', source: 'fallback', country: null }
 */
export const detectLanguage = async () => {
  // 1. Check URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lng');
  if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang)) {
    console.log(`[Language Detection] Using URL parameter: ${urlLang}`);
    return { language: urlLang, source: 'url', country: null };
  }

  // 2. Check localStorage (user's previous explicit choice)
  const storedLang = getStoredLanguage();
  if (storedLang) {
    console.log(`[Language Detection] Using stored preference: ${storedLang}`);
    return { language: storedLang, source: 'localStorage', country: null };
  }

  // 3. Fallback to English (ALWAYS - no automatic browser/IP detection)
  // German and other languages require explicit user selection
  console.log('[Language Detection] Falling back to English (default)');
  return { language: 'en', source: 'fallback', country: null };
};

/**
 * Store user's language preference
 * @param {string} language - Language code
 */
export const storeLanguagePreference = (language) => {
  if (SUPPORTED_LANGUAGES.includes(language)) {
    localStorage.setItem('scrolled_preferred_language', language);
    console.log(`[Language Detection] Stored preference: ${language}`);
  }
};

/**
 * Get all supported languages
 * @returns {Array} Array of language codes
 */
export const getSupportedLanguages = () => {
  return SUPPORTED_LANGUAGES;
};

/**
 * Log detection details (for debugging)
 */
export const logDetectionDebug = async () => {
  console.group('🌍 Language Detection Debug Info');
  console.log('Browser language:', navigator.language);
  console.log('Device language (mapped):', getBrowserLanguage());
  console.log('Stored language:', getStoredLanguage());

  try {
    const country = await getCountryFromIP();
    const geoLang = getLanguageFromCountry(country);
    console.log('IP Country:', country);
    console.log('Geolocation language:', geoLang);
  } catch (error) {
    console.log('Geolocation error:', error.message);
  }

  console.groupEnd();
};

export default {
  detectLanguage,
  storeLanguagePreference,
  getBrowserLanguage,
  getStoredLanguage,
  getCountryFromIP,
  getLanguageFromCountry,
  getSupportedLanguages,
  logDetectionDebug,
};
